import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, CreditCard, CheckCircle } from 'lucide-react';
import { Wallet } from '@mercadopago/sdk-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Separator } from './ui/separator';
import { ordersAPI } from '../services/api';
import { toast } from 'sonner';
import axios from 'axios';

const SHIPPING_COST = 200;

export const Checkout = () => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [preferenceId, setPreferenceId] = useState(null);
  const [creatingPreference, setCreatingPreference] = useState(false);

  const [formData, setFormData] = useState({
    // Información personal
    fullName: '',
    email: '',
    phone: '',
    // Dirección
    street: '',
    number: '',
    colony: '',
    zipCode: '',
    city: '',
    state: '',
    references: '',
    // Método de pago
    paymentMethod: 'mercadopago'
  });

  useEffect(() => {
    const savedCart = localStorage.getItem('burbujasCart');
    if (savedCart) {
      const items = JSON.parse(savedCart);
      setCartItems(items);
      if (items.length === 0) {
        navigate('/');
      }
    } else {
      navigate('/');
    }
  }, [navigate]);

  const subtotal = cartItems.reduce(
    (sum, item) => sum + item.selectedVariant.price * item.quantity,
    0
  );
  const shipping = SHIPPING_COST;
  const total = subtotal + shipping;

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCreatingPreference(true);

    try {
      // Preparar items para MercadoPago
      const items = cartItems.map(item => ({
        title: item.name,
        description: `${item.selectedVariant.size} - ${item.category}`,
        quantity: item.quantity,
        unit_price: item.selectedVariant.price,
        currency_id: 'MXN'
      }));

      // Preparar items detallados para persistir el pedido en DB
      const orderItems = cartItems.map(item => ({
        product_id: item.id,
        product_name: item.name,
        variant_size: item.selectedVariant.size,
        variant_price: item.selectedVariant.price,
        quantity: item.quantity,
      }));

      // Separar nombre completo en nombre y apellido
      const nameParts = formData.fullName.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || firstName;

      // Crear preferencia de pago en MercadoPago (esto también persiste el pedido en DB)
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/mercadopago/create-preference`,
        {
          items: items,
          order_items: orderItems,
          references: formData.references || null,
          payer: {
            name: firstName,
            surname: lastName,
            email: formData.email,
            phone: formData.phone,
            address: `${formData.street} ${formData.number}, Col. ${formData.colony}`,
            city: formData.city,
            state: formData.state,
            zip_code: formData.zipCode
          }
        }
      );

      if (response.data.success) {
        setPreferenceId(response.data.preference_id);
        toast.success('¡Listo para pagar!', {
          description: 'Haz clic en el botón de Mercado Pago para completar tu compra.'
        });
      } else {
        throw new Error(response.data.error || 'Error desconocido');
      }

    } catch (error) {
      console.error('Error creating preference:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Error desconocido';
      toast.error('Error al preparar el pago', {
        description: `${errorMessage}. Por favor intenta de nuevo o contáctanos por WhatsApp.`
      });
    } finally {
      setCreatingPreference(false);
    }
  };

  const handlePaymentSuccess = async () => {
    // Crear orden en nuestra base de datos
    try {
      const orderItems = cartItems.map(item => ({
        product_id: item.id,
        product_name: item.name,
        variant_size: item.selectedVariant.size,
        variant_price: item.selectedVariant.price,
        quantity: item.quantity,
        subtotal: item.selectedVariant.price * item.quantity
      }));

      const orderData = {
        customer_info: {
          name: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          address: `${formData.street} ${formData.number}, Col. ${formData.colony}`,
          city: formData.city,
          state: formData.state,
          zip_code: formData.zipCode
        },
        items: orderItems,
        payment_method: 'mercadopago'
      };

      const order = await ordersAPI.create(orderData);
      setOrderId(order.id);
      setOrderComplete(true);
      
      // Limpiar carrito
      localStorage.removeItem('burbujasCart');
      window.dispatchEvent(new CustomEvent('cartUpdate', { detail: 0 }));

    } catch (error) {
      console.error('Error creating order:', error);
    }
  };

  if (orderComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-honey-50 to-white pt-24 pb-12">
        <div className="container mx-auto px-4">
          <Card className="max-w-2xl mx-auto border-honey-200">
            <CardContent className="p-12 text-center">
              <CheckCircle className="w-20 h-20 text-green-600 mx-auto mb-6" />
              <h1 className="text-3xl font-bold text-honey-950 mb-4">
                ¡Pedido Recibido!
              </h1>
              <p className="text-lg text-honey-900/80 mb-6">
                Gracias por tu compra. Hemos recibido tu pedido y nos pondremos en contacto contigo pronto.
              </p>
              <div className="bg-honey-50 rounded-lg p-6 mb-6">
                <p className="text-sm text-honey-900/70 mb-2">Número de pedido:</p>
                <p className="text-xl font-mono font-bold text-honey-900">{orderId}</p>
              </div>
              <p className="text-honey-900/70 mb-8">
                Te enviaremos un correo de confirmación a <strong>{formData.email}</strong>
              </p>
              <Button
                onClick={() => navigate('/')}
                className="bg-honey-600 hover:bg-honey-700 text-white"
              >
                Volver al inicio
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-honey-50 to-white pt-24 pb-12">
      <div className="container mx-auto px-4">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-6 text-honey-900 hover:text-honey-700"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver a la tienda
        </Button>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Formulario */}
          <div className="lg:col-span-2">
            <Card className="border-honey-200">
              <CardHeader>
                <CardTitle className="text-2xl text-honey-950">Información de envío</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Información Personal */}
                  <div>
                    <h3 className="text-lg font-semibold text-honey-950 mb-4 flex items-center">
                      <Package className="w-5 h-5 mr-2" />
                      Datos personales
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="fullName">Nombre completo *</Label>
                        <Input
                          id="fullName"
                          name="fullName"
                          value={formData.fullName}
                          onChange={handleInputChange}
                          placeholder="Juan Pérez"
                          required
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone">Teléfono *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="33 1234 5678"
                          required
                          className="mt-2"
                        />
                      </div>
                    </div>
                    <div className="mt-4">
                      <Label htmlFor="email">Correo electrónico *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="correo@ejemplo.com"
                        required
                        className="mt-2"
                      />
                    </div>
                  </div>

                  <Separator />

                  {/* Dirección */}
                  <div>
                    <h3 className="text-lg font-semibold text-honey-950 mb-4">
                      Dirección de envío
                    </h3>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="md:col-span-2">
                        <Label htmlFor="street">Calle *</Label>
                        <Input
                          id="street"
                          name="street"
                          value={formData.street}
                          onChange={handleInputChange}
                          placeholder="Av. Juárez"
                          required
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="number">Número *</Label>
                        <Input
                          id="number"
                          name="number"
                          value={formData.number}
                          onChange={handleInputChange}
                          placeholder="123"
                          required
                          className="mt-2"
                        />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4 mt-4">
                      <div>
                        <Label htmlFor="colony">Colonia *</Label>
                        <Input
                          id="colony"
                          name="colony"
                          value={formData.colony}
                          onChange={handleInputChange}
                          placeholder="Centro"
                          required
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="zipCode">Código Postal *</Label>
                        <Input
                          id="zipCode"
                          name="zipCode"
                          value={formData.zipCode}
                          onChange={handleInputChange}
                          placeholder="44100"
                          required
                          className="mt-2"
                        />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4 mt-4">
                      <div>
                        <Label htmlFor="city">Ciudad *</Label>
                        <Input
                          id="city"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          placeholder="Guadalajara"
                          required
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="state">Estado *</Label>
                        <Input
                          id="state"
                          name="state"
                          value={formData.state}
                          onChange={handleInputChange}
                          placeholder="Jalisco"
                          required
                          className="mt-2"
                        />
                      </div>
                    </div>
                    <div className="mt-4">
                      <Label htmlFor="references">Referencias (opcional)</Label>
                      <Input
                        id="references"
                        name="references"
                        value={formData.references}
                        onChange={handleInputChange}
                        placeholder="Casa color azul, portón negro..."
                        className="mt-2"
                      />
                    </div>
                  </div>

                  <Separator />

                  {/* Método de pago */}
                  <div>
                    <h3 className="text-lg font-semibold text-honey-950 mb-4 flex items-center">
                      <CreditCard className="w-5 h-5 mr-2" />
                      Método de pago
                    </h3>
                    <div className="bg-honey-50 border border-honey-200 rounded-lg p-4">
                      <p className="text-honey-900 mb-2">
                        <strong>Mercado Pago</strong>
                      </p>
                      <p className="text-sm text-honey-900/70">
                        Paga de forma segura con tarjeta, OXXO, transferencia bancaria o efectivo.
                      </p>
                    </div>
                  </div>

                  {!preferenceId ? (
                    <Button
                      type="submit"
                      disabled={creatingPreference}
                      className="w-full bg-honey-600 hover:bg-honey-700 text-white py-6 text-lg font-semibold"
                    >
                      {creatingPreference ? 'Preparando pago...' : 'Continuar al pago'}
                    </Button>
                  ) : (
                    <div className="space-y-4">
                      <p className="text-center text-honey-900 font-medium">
                        Completa tu pago con Mercado Pago
                      </p>
                      <Wallet
                        initialization={{ preferenceId: preferenceId }}
                        onReady={() => console.log('Wallet ready')}
                        onError={(error) => {
                          console.error('Wallet error:', error);
                          toast.error('Error al cargar el checkout');
                        }}
                        customization={{
                          texts: {
                            valueProp: 'smart_option'
                          }
                        }}
                      />
                    </div>
                  )}
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Resumen del pedido */}
          <div>
            <Card className="border-honey-200 sticky top-24">
              <CardHeader>
                <CardTitle className="text-xl text-honey-950">Resumen del pedido</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Items */}
                <div className="space-y-3">
                  {cartItems.map((item) => (
                    <div key={item.cartItemId} className="flex gap-3">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-16 h-16 object-cover rounded"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-honey-950">{item.name}</p>
                        <p className="text-xs text-honey-700">{item.selectedVariant.size}</p>
                        <p className="text-xs text-honey-700">Cantidad: {item.quantity}</p>
                      </div>
                      <p className="text-sm font-semibold text-honey-900">
                        ${item.selectedVariant.price * item.quantity}
                      </p>
                    </div>
                  ))}
                </div>

                <Separator />

                {/* Totales */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-honey-900/70">Subtotal</span>
                    <span className="font-medium text-honey-950">${subtotal} MXN</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-honey-900/70">Envío</span>
                    <span className="font-medium text-honey-950">${shipping} MXN</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between text-lg font-bold">
                    <span className="text-honey-950">Total</span>
                    <span className="text-honey-900">${total} MXN</span>
                  </div>
                </div>

                <div className="bg-honey-50 rounded-lg p-3">
                  <p className="text-xs text-honey-900/70">
                    <strong>Tiempo de entrega:</strong> 3-7 días hábiles
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
